import React, { useCallback, useEffect, useRef } from 'react'
import { useField } from 'formik'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FormHelperText } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { SITE_RECAPTCHA_KEY } from '../../../constants'

type FormikRecaptchaProps = {
  name: string
}

// reCAPTCHA v3 tokens are single-use and only valid for ~2 minutes after being minted. Since
// this field fetches its token once on mount rather than right before submit, a form left open
// longer than that would otherwise submit a stale token. Refresh proactively, well within that
// window, so a valid token is always ready whenever the user actually submits.
const TOKEN_REFRESH_INTERVAL_MS = 90_000

const FormikRecaptchaField: React.FC<FormikRecaptchaProps> = props => {
  const { t } = useTranslation()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { enqueueSnackbar } = useSnackbar()
  const [, { error, touched }, { setTouched, setValue, setError }] = useField(props.name)
  // Tracks whether we've ever obtained a token, so the effect below only reacts to a token
  // actually expiring/failing later on, not to the field's initial "required" state before the
  // first verification round-trip resolves.
  const hasVerifiedOnce = useRef(false)

  const handleReCaptchaVerify = useCallback(async (): Promise<boolean> => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return false
    }
    try {
      const token = await executeRecaptcha()
      setValue(token)
      setTouched(true)
      setError(undefined)
      hasVerifiedOnce.current = true
      return true
    } catch (e) {
      console.error('reCAPTCHA verification failed', e)
      return false
    }
  }, [executeRecaptcha])

  useEffect(() => {
    handleReCaptchaVerify()
  }, [handleReCaptchaVerify])

  useEffect(() => {
    const interval = setInterval(handleReCaptchaVerify, TOKEN_REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [handleReCaptchaVerify])

  useEffect(() => {
    if (error && hasVerifiedOnce.current) {
      handleReCaptchaVerify().then(success => {
        if (success) {
          enqueueSnackbar(
            t('snackbarMessages.error.recaptchaExpired', {
              defaultValue: '{{error}}. Please try submitting the form again.',
              error,
            }),
            { variant: 'error', autoHideDuration: 2000 }
          )
        }
      })
    }
  }, [error])

  return (
    <>
      <FormHelperText>
        <Trans
          i18nKey='formLabels.captcha'
          defaults={`This site is protected by reCAPTCHA and the Google <a1>Privacy Policy</a1> and <a2>Terms of Service</a2> apply.`}
          components={{
            a1: <a href='https://policies.google.com/privacy' target='_blank' />,
            a2: <a href='https://policies.google.com/terms' target='_blank' />,
          }}
        />
      </FormHelperText>
      {touched && error && (
        <FormHelperText className='px-14' error={true}>
          {error}
        </FormHelperText>
      )}
    </>
  )
}

// Scopes the reCAPTCHA script/provider to only the forms that actually need it, instead of
// loading it (671 KiB, ~350ms CPU) for every page via a root-level provider.
const FormikRecaptcha: React.FC<FormikRecaptchaProps> = props => {
  const { i18n } = useTranslation()

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={SITE_RECAPTCHA_KEY}
      language={i18n.language}
      useEnterprise={true}
      scriptProps={{
        async: true,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <FormikRecaptchaField {...props} />
    </GoogleReCaptchaProvider>
  )
}

export default FormikRecaptcha
